<template>
  <h4
    class="text-center text-pageheadercolor"
    v-if="donor.donorTypeCd === DonorTypeEnum.DT_P"
  >
    {{ donor.givenName }} {{ donor.familyName }} -
    {{ userRoleToName(store.state.user) }} Dashboard
  </h4>
  <h4 class="text-center text-pageheadercolor" v-else>
    {{ (donor as BusinessDonorViewModel).businessName }} -
    {{ userRoleToName(store.state.user) }} Dashboard
  </h4>

  <div style="max-width: 1000px" class="q-mx-auto">
    <CSCard class="full-width">
      <CSCardSection v-if="donor.donationStatusCd !== 'DS_A'">
        <div class="row justify-center">
          <p class="text-body1 text-weight-medium">Donations are not active</p>
        </div>
        <div class="row">
          <CSAsyncButton @click="onActivateDonations" class="q-mx-auto">
            Activate Donations
          </CSAsyncButton>
        </div>
      </CSCardSection>
      <CSCardSection v-else>
        <div class="row justify-center">
          <p class="text-body1 text-weight-medium">
            Thank you for your
            {{
              mapReferenceCodeToName(
                store.state.referenceData.paymentFrequencies,
                donor.paymentFrequencyCd,
              ).toLowerCase()
            }}
            donation of {{ formatMonetaryAmt(donor.donationAmt) }} for
            {{ donor.recognisedSchool.name }}.
          </p>
        </div>
      </CSCardSection>
      <q-separator inset />
      <CSCardSection>
        <div class="row" v-if="donor.donorTypeCd === DonorTypeEnum.DT_B">
          <span class="text-weight-medium" style="width: 150px">
            Business Name:
          </span>
          <span class="col"
            >{{ (donor as BusinessDonorViewModel).businessName }}
          </span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Name: </span>
          <span class="col">{{ donor.givenName }} {{ donor.familyName }}</span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Email: </span>
          <span class="col">{{ donor.emailAddress }}</span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Phone: </span>
          <span class="col">{{
            formatPhoneNumber(donor.mobilePhoneNumber)
          }}</span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px">
            State/Territory:
          </span>
          <span class="col">{{ donor.australianStateCd }}</span>
        </div>
      </CSCardSection>
    </CSCard>
  </div>
</template>

<script setup lang="ts">
import CSAsyncButton from "@/components/CharitySystem/CSAsyncButton.vue";
import CSCard from "@/components/CharitySystem/CSCard.vue";
import CSCardSection from "@/components/CharitySystem/CSCardSection.vue";

import {
  BusinessDonorViewModel,
  PrivateDonorViewModel,
} from "@/models/ViewModels/DonorModel";

import { onMounted, reactive } from "vue";
import { useQuasar } from "quasar";
import { showErrorNotification } from "@/components/shared/dialog";
import DonorUseCase from "@/usecases/DonorUseCase";
import { useStore } from "@/store";
import {
  processResponse,
  formatPhoneNumber,
  mapReferenceCodeToName,
  userRoleToName,
  formatMonetaryAmt,
} from "@/shared/helperFunctions";
import router from "@/router";
import { DonorTypeEnum } from "@/shared/ModelEnums";
import { ApiException, createApiException } from "@/api/ResponseModel";

const donorUseCase = new DonorUseCase();
const $q = useQuasar();
const store = useStore();
const donor = reactive<PrivateDonorViewModel | BusinessDonorViewModel>(
  new PrivateDonorViewModel(),
);

onMounted(async () => {
  try {
    const userReference = store.state.user?.userReference as string;

    if (userReference) {
      const result = await donorUseCase.getByReference(userReference);

      processResponse(result, (content) => Object.assign(donor, content));
    } else {
      throw createApiException(
        "User not logged in",
        "You must be logged in to view Donor details.",
      );
    }
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  }
});

async function onActivateDonations() {
  if (donor.donationStatusCd === "DS_I") router.push("/donor/donationdetails/");
  else if (donor.donationStatusCd === "DS_P") {
    const updateDonor = { ...donor };
    updateDonor.donationStatusCd = "DS_A";
    const result = await donorUseCase.updateDonationPayment(
      donor.donationAmt,
      updateDonor,
      updateDonor.paymentFrequencyCd,
    );
    processResponse(result, (content) => {
      Object.assign(donor, content);
    });
  }
}
</script>
